import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useParams } from 'react-router';
import type { MyPlanDto, PauseRequestStatus } from '@tiffinify/contracts';
import { addMonths, formatDay, formatMonth, formatRanges, mealsOf, waLink, type CalendarCell, type Slot } from '@tiffinify/core';
import { Badge, BtnPrimary, BtnSecondary, Card, CalHeader, CardTitle, Empty, ErrorText, Helper, Hero, Legend, LoadError, Logo, Mono, MonthGrid, PageLoader, SegBar } from '../components/ui';
import { Back, Btn, Card as PCard, Foot, Ic, Note, Row, Screen, Seg, Title, Top } from '../components/p';
import { del, get, isNotFound, post } from '../lib/api';
import { clock12, errorMessage, formatInr, mealsLabel } from '../lib/format';

const CAL_CLASS: Record<string, string> = {
  none: '', off: 'off', holiday: 'holiday', delivered: 'done', today: 'today', paused: 'paused', partial: 'partial', upcoming: 'upcoming', end: 'end',
};
/** Days a customer can ask to pause: meals still to come, not already off. */
const PAUSABLE = new Set(['upcoming', 'end', 'partial']);

const STATUS: Record<PauseRequestStatus, { label: string; kind: 'saffron' | 'ok' | 'neutral' | 'end' }> = {
  waiting: { label: 'Waiting', kind: 'saffron' },
  accepted: { label: 'Paused', kind: 'ok' },
  declined: { label: 'Not possible', kind: 'end' },
  cancelled: { label: 'Cancelled', kind: 'neutral' },
};

/** The customer's own page, opened from the link the kitchen sends. No sign-in: the link is the key. */
export function MyPlan() {
  const { token = '' } = useParams();
  const qc = useQueryClient();
  const [month, setMonth] = useState<string | undefined>(undefined);
  const [showCalendar, setShowCalendar] = useState(false);
  const [picking, setPicking] = useState(false);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [meals, setMeals] = useState<Slot | null>(null);

  const q = useQuery({
    queryKey: ['my-plan', token, month],
    queryFn: () => get<MyPlanDto>(`/my/${encodeURIComponent(token)}${month ? `?month=${month}` : ''}`),
    placeholderData: keepPreviousData,
    retry: (n, e) => !isNotFound(e) && n < 2,
  });
  const d = q.data;
  const refresh = () => void qc.invalidateQueries({ queryKey: ['my-plan', token] });

  const send = useMutation({
    mutationFn: () => post<MyPlanDto>(`/my/${encodeURIComponent(token)}/pause-requests`, { slot: meals ?? d!.plan!.slot, days: [...picked].sort() }),
    onSuccess: () => { setPicking(false); setPicked(new Set()); refresh(); },
  });
  const cancel = useMutation({
    mutationFn: (id: string) => del<MyPlanDto>(`/my/${encodeURIComponent(token)}/pause-requests/${id}`),
    onSuccess: refresh,
  });

  const logo = <div className="public-logo"><Logo size={18} /></div>;
  if (q.isPending) return <main className="main task my-plan">{logo}<PageLoader label="Opening your plan" /></main>;
  if (!d) {
    return (
      <main className="main task my-plan">
        {logo}
        {isNotFound(q.error)
          ? <Card><Empty title="This page isn’t available">Ask your kitchen to send you a new link.</Empty></Card>
          : <LoadError what="your plan" onRetry={() => void q.refetch()} />}
      </main>
    );
  }

  const plan = d.plan;
  const sm = plan?.summary;
  const talk = (text: string) => (d.kitchenWhatsApp ? waLink(d.kitchenWhatsApp, text) : '');
  const active = !!plan && !plan.closed && sm!.state !== 'ended';
  const waiting = d.requests.filter((r) => r.status === 'waiting');
  const answered = d.requests.filter((r) => r.status !== 'waiting').slice(0, 3);
  const planMeals = plan ? mealsOf(plan.slot) : [];

  const pickable = (c: CalendarCell) => picking && c.date >= d.rules.firstDay && PAUSABLE.has(c.state);
  const classFor = (c: CalendarCell) => (picked.has(c.date) ? 'sel' : CAL_CLASS[c.state] ?? '');
  const toggle = (day: string) => setPicked((s) => { const n = new Set(s); n.has(day) ? n.delete(day) : n.add(day); return n; });
  const shown = plan?.calendar.month;

  if (picking && active && plan && shown) {
    return (
      <Screen gap={18} className="p-pause-request">
        <Top><Back label="My plan" onClick={() => { setPicking(false); setPicked(new Set()); send.reset(); }} /></Top>
        <Title h="Ask to pause" sub="Pick the days. The kitchen confirms on WhatsApp." />
        <Card pad="sm">
          <CalHeader title={formatMonth(shown)} onPrev={() => setMonth(addMonths(shown, -1))} onNext={() => setMonth(addMonths(shown, 1))} />
          <MonthGrid cells={plan.calendar.cells} aria={`Pick days to pause in ${formatMonth(shown)}`}
            onPick={toggle} classFor={classFor} disabledFor={(cell) => !pickable(cell)} />
        </Card>
        {planMeals.length > 1 && <Seg label="Meals to pause" value={meals ?? plan.slot} onChange={setMeals}
          options={[{value: plan.slot, label: planMeals.length > 2 ? 'All meals' : mealsLabel(plan.slot)}, ...planMeals.map((meal) => ({value: meal as Slot, label: mealsLabel(meal as Slot)}))]} />}
        <Note icon="clock">Requests close at {clock12(d.rules.cutoff)} the day before. Paused meals move to the end of your plan.</Note>
        <ErrorText>{send.error ? errorMessage(send.error) : ''}</ErrorText>
        <Btn label={picked.size ? `Send request · ${picked.size} ${picked.size === 1 ? 'day' : 'days'}` : 'Pick days on the calendar'}
          go="send" busy={send.isPending} disabled={!picked.size} onClick={() => send.mutate()} />
        <Foot />
      </Screen>
    );
  }

  return (
    <main className="main task my-plan">
      <header className="my-plan-head">
        <Mono muted>Your plan with</Mono>
        <h1>{d.kitchenName}</h1>
      </header>

      {!plan || !sm ? (
        <Card>
          <Empty title={`Hi ${d.firstName}`}>You don’t have a plan with {d.kitchenName} right now.</Empty>
          {d.kitchenWhatsApp && <div className="my-plan-card-cta"><BtnSecondary label={`Message ${d.kitchenName}`} icon="chat" href={talk(`Hi, this is ${d.firstName}. I’d like to start a plan.`)} /></div>}
        </Card>
      ) : (
        <>
          <Hero label="Your plan">
            <div className="between"><Mono muted>Hi {d.firstName}</Mono><Mono muted>{plan.name}</Mono></div>
            {plan.closed ? (
              <div className="number-row"><span className="big">{sm.delivered}</span><span><b>meals delivered</b><small>This plan is closed</small></span></div>
            ) : (
              <div className="number-row"><span className="big">{sm.left}</span><span><b>{sm.left === 1 ? 'meal' : 'meals'} left</b><small>{mealsLabel(plan.slot)}</small></span></div>
            )}
            <SegBar done={sm.delivered} total={sm.totalMeals} />
            <div className="hero-facts pair">
              <div className="hero-fact"><Mono muted>Started</Mono><b>{formatDay(sm.startDate)}</b></div>
              <div className="hero-fact">
                <Mono muted>Ends</Mono><b>{formatDay(sm.endDate)}</b>
                {sm.pausedDays > 0 && <small className="saffron">+{sm.pausedDays} paused days</small>}
              </div>
            </div>
          </Hero>

          {d.due > 0 && (
            <Card pad>
              <CardTitle title="To pay" right={<Badge kind="due">{formatInr(d.due)}</Badge>} />
              {d.payLink ? (
                <>
                  <BtnPrimary label={`Pay ${formatInr(d.due)} by UPI`} icon="rupee" href={d.payLink} />
                  <Helper>Opens your UPI app on this phone. {d.kitchenName} marks it paid once the money arrives.</Helper>
                </>
              ) : <Helper>Pay {d.kitchenName} by UPI or cash. It shows here as paid once they record it.</Helper>}
            </Card>
          )}

          {d.menus.length > 0 && (
            <PCard>
              {d.menus.map((m) => (
                <Row key={m.code} to={`/k/${d.kitchenCode}/${m.code}`} left={<Ic icon="receipt" tone="s" />} t={`${m.name} is open`}
                  s={m.closesAt ? `Order by ${formatDay(m.closesAt.slice(0, 10))}, ${clock12(m.closesAt.slice(11, 16))}` : `Order from ${d.kitchenName}`} chev />
              ))}
            </PCard>
          )}

          {(waiting.length > 0 || answered.length > 0) && (
            <Card pad>
              <CardTitle title="Pause requests" />
              <div className="my-plan-requests">
                {[...waiting, ...answered].map((r) => (
                  <div key={r.id} className="my-plan-request">
                    <span className="grow">
                      <b>{formatRanges(r.days)}</b>
                      <small>{mealsLabel(r.slot)}{r.status === 'waiting' ? ` · waiting for ${d.kitchenName}` : ''}</small>
                    </span>
                    <Badge kind={STATUS[r.status].kind}>{STATUS[r.status].label}</Badge>
                    {r.status === 'waiting' && (
                      <button type="button" className="link" disabled={cancel.isPending} onClick={() => cancel.mutate(r.id)}>Cancel</button>
                    )}
                  </div>
                ))}
              </div>
              <ErrorText>{cancel.error ? errorMessage(cancel.error) : ''}</ErrorText>
            </Card>
          )}

          <PCard>
            {active && <Row left={<Ic icon="pause" tone="s" />} t="Ask to pause days" s="The kitchen confirms your request" onClick={() => setPicking(true)} chev />}
            <Row left={<Ic icon="calendar" />} t={showCalendar ? 'Hide my calendar' : 'See my calendar'} s="Meals, paused days and the plan’s end" onClick={() => setShowCalendar(!showCalendar)} chev />
          </PCard>

          {shown && showCalendar && (
            <Card pad="sm">
              <CalHeader title={formatMonth(shown)} onPrev={() => setMonth(addMonths(shown, -1))} onNext={() => setMonth(addMonths(shown, 1))} />
              <MonthGrid
                cells={plan.calendar.cells}
                aria={picking ? `Pick days to pause in ${formatMonth(shown)}` : `${formatMonth(shown)} meal calendar`}
                onPick={picking ? toggle : undefined}
                classFor={classFor}
                disabledFor={(c) => !pickable(c)}
              />
              <Legend items={picking
                ? [{ cls: 'sel', label: 'To pause' }, { cls: 'paused', label: 'Paused' }, { cls: '', label: 'Coming up' }, { cls: 'end', label: 'Ends' }]
                : [{ cls: 'done', label: 'Delivered' }, { cls: 'paused', label: 'Paused' }, { cls: '', label: 'Coming up' }, { cls: 'end', label: 'Ends' }]} />
            </Card>
          )}


          {d.kitchenWhatsApp && (sm.daysToEnd <= 7 || !active) && (
            <Card pad>
              <CardTitle title={active ? 'Renew your plan' : 'Want to continue?'} />
              <Helper>{active ? `Your plan ends on ${formatDay(sm.endDate)}. Message ${d.kitchenName} to keep your meals coming.` : `Message ${d.kitchenName} to start again.`}</Helper>
              <BtnSecondary label="Renew on WhatsApp" icon="refresh" href={talk(`Hi, this is ${d.firstName}. I’d like to renew my ${plan.name} plan.`)} />
            </Card>
          )}
        </>
      )}

      <footer className="my-plan-foot">
        <span>This page is only for you. Please don’t share the link.</span>
        <span className="my-plan-by"><span>Powered by</span><Logo size={12} /></span>
      </footer>
    </main>
  );
}
